import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app'
import {
  browserLocalPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  signInAnonymously,
  type Auth,
} from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { getFunctions, type Functions } from 'firebase/functions'
import { getStorage, type FirebaseStorage } from 'firebase/storage'
import { resolveStorageBucket } from './firebaseStorageBucket'

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
  storageBucket: string
}

function getEnvConfig(): FirebaseConfig | null {
  const apiKey = (import.meta.env.VITE_FIREBASE_API_KEY as string | undefined)?.trim()
  const authDomain = (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined)?.trim()
  const projectId = (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined)?.trim()
  const appId = (import.meta.env.VITE_FIREBASE_APP_ID as string | undefined)?.trim()
  if (!apiKey || !authDomain || !projectId || !appId) return null
  const storageBucket = resolveStorageBucket(
    projectId,
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined,
  )
  return { apiKey, authDomain, projectId, appId, storageBucket }
}

let app: FirebaseApp | null = null
let firestoreInstance: Firestore | null = null
let authInstance: Auth | null = null
let functionsInstance: Functions | null = null
let storageInstance: FirebaseStorage | null = null

/** Get or create Firebase app when config is present. */
export function getFirebaseApp(): FirebaseApp | null {
  if (app) return app
  const config = getEnvConfig()
  if (!config) return null
  app = getApps().length ? (getApp() as FirebaseApp) : initializeApp(config)
  return app
}

/** Whether the app should use Firestore (env says so and config is present). */
export function useFirestore(): boolean {
  const flag = import.meta.env.VITE_USE_FIRESTORE === 'true'
  const config = getEnvConfig()
  return Boolean(flag && config)
}

/** No-op kept for callers; persistence is set via initializeAuth. */
export async function ensureAuthPersistence(): Promise<void> {}

/** Get Firebase Auth instance. Returns null if Firebase config is missing. */
export function getFirebaseAuth(): Auth | null {
  if (authInstance) return authInstance
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) return null
  try {
    authInstance = initializeAuth(firebaseApp, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
    })
  } catch {
    // Hot reload / duplicate init — fall back to existing default auth instance.
    authInstance = getAuth(firebaseApp)
  }
  return authInstance
}

/**
 * Anonymous auth for contexts that only need `request.auth != null` (e.g. wall display heartbeats).
 */
export async function ensureAnonymousAuth(): Promise<void> {
  const auth = getFirebaseAuth()
  if (!auth) return
  if (auth.currentUser) return
  await signInAnonymously(auth)
}

/** Initialize Firebase app and return Firestore. Only call when useFirestore() is true. */
export function getFirestoreInstance(): Firestore {
  if (firestoreInstance) return firestoreInstance
  const config = getEnvConfig()
  if (!config) throw new Error('Firebase config missing; check .env and useFirestore()')
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) throw new Error('Firebase config missing; check .env')
  firestoreInstance = getFirestore(firebaseApp)
  return firestoreInstance
}

const DEFAULT_FUNCTIONS_REGION =
  (import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined)?.trim() ||
  'asia-southeast1'

/** Firebase Cloud Functions for callable backends (e.g. Midtrans). */
export function getFirebaseFunctions(): Functions | null {
  if (functionsInstance) return functionsInstance
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) return null
  functionsInstance = getFunctions(firebaseApp, DEFAULT_FUNCTIONS_REGION)
  return functionsInstance
}

/** Firebase Storage for place logos and other assets. */
export function getFirebaseStorage(): FirebaseStorage | null {
  if (storageInstance) return storageInstance
  const firebaseApp = getFirebaseApp()
  if (!firebaseApp) return null
  storageInstance = getStorage(firebaseApp)
  return storageInstance
}
